import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import { ShieldCheck, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = request code, 2 = enter code + new password
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setInfo("If that email is registered, a 6-digit code is on its way. Check your inbox (and spam).");
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      setInfo("Password updated. Redirecting to sign in…");
      setTimeout(() => nav("/login"), 1400);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 fade-up">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-700 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <div className="font-display text-xl font-bold text-slate-900">SafeHaven</div>
            <div className="text-xs text-slate-500">Reset your password</div>
          </div>
        </div>

        {step === 1 ? (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Forgot password</h1>
            <p className="text-sm text-slate-500 mb-6">Enter your email and we&apos;ll send a 6-digit reset code.</p>
            <form onSubmit={requestCode} className="space-y-3">
              <div>
                <label className="text-sm text-slate-700 font-medium">Email</label>
                <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" placeholder="you@example.com" />
              </div>
              {error && <div className="text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-xl" data-testid="forgot-error">{error}</div>}
              <button data-testid="forgot-send-code" disabled={loading} className="w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl py-3 font-semibold transition-colors">
                {loading ? "Sending…" : "Send code"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Enter your code</h1>
            <p className="text-sm text-slate-500 mb-4">We sent a 6-digit code to {email}. It expires in 15 minutes.</p>
            {info && <div className="flex items-center gap-2 text-sm bg-teal-50 text-teal-800 border border-teal-200 px-3 py-2 rounded-xl mb-3" data-testid="forgot-info"><MailCheck className="w-4 h-4 shrink-0" />{info}</div>}
            <form onSubmit={resetPassword} className="space-y-3">
              <div>
                <label className="text-sm text-slate-700 font-medium">6-digit code</label>
                <input data-testid="reset-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 tracking-[0.5em] text-center text-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" placeholder="••••••" />
              </div>
              <div>
                <label className="text-sm text-slate-700 font-medium">New password</label>
                <PasswordInput testId="reset-new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" />
              </div>
              {error && <div className="text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-xl" data-testid="reset-error">{error}</div>}
              <button data-testid="reset-submit" disabled={loading || code.length !== 6} className="w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl py-3 font-semibold transition-colors">
                {loading ? "Resetting…" : "Reset password"}
              </button>
              <button type="button" data-testid="forgot-resend" onClick={requestCode} className="w-full text-sm text-teal-700 hover:underline">Resend code</button>
            </form>
          </>
        )}

        <p className="text-sm text-slate-500 mt-6 text-center">
          <Link data-testid="forgot-to-login" to="/login" className="text-teal-700 font-medium hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
